import { ButtonHTMLAttributes, ReactNode, forwardRef, useState } from 'react';

export interface SelectableRowProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Highlights the row as the active selection (accent-glow background + accent text). */
  selected?: boolean;
  /** Render the 3px accent left-bar that toggles with `selected`. Default `true`. */
  accentBar?: boolean;
  /** Bold the label while selected. Default `true`. */
  boldWhenSelected?: boolean;
  children: ReactNode;
}

/**
 * Full-width, left-aligned selectable list row used by the explorer sidebars
 * (table pickers, file/folder browsers). Owns the shared "look" — bottom
 * divider, accent-glow + accent-text when selected, an optional 3px accent
 * left-bar, and a neutral `--bg-tertiary` hover when not selected. Layout and
 * spacing stay with the caller via `className` (Tailwind lane), so the same row
 * can host a one-line file entry or a two-line table summary.
 */
export const SelectableRow = forwardRef<HTMLButtonElement, SelectableRowProps>(
  function SelectableRow(
    { selected = false, accentBar = true, boldWhenSelected = true, className, style, children, ...rest },
    ref,
  ) {
    const [hover, setHover] = useState(false);
    return (
      <button
        ref={ref}
        type="button"
        className={`w-full text-left transition-all${className ? ` ${className}` : ''}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          borderBottom: '1px solid var(--border)',
          ...(accentBar
            ? { borderLeft: selected ? '3px solid var(--accent)' : '3px solid transparent' }
            : null),
          background: selected ? 'var(--accent-glow)' : hover ? 'var(--bg-tertiary)' : 'transparent',
          color: selected ? 'var(--accent)' : 'var(--text-primary)',
          fontWeight: boldWhenSelected && selected ? 600 : undefined,
          ...style,
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
